// Feature 08: Link Impact Attribution Service

import { api } from './api';
import { LinkImpactData, LinkEvent } from '../types/linkImpact.types';

export interface LinkImpactParams {
  pageId: string;
  engine?: 'google' | 'bing';
  baselineDays?: number; // Default: 30
  outcomeDays?: number; // Default: 30
}

export interface LinkEventCreateParams {
  pageId: string;
  linkUrl: string;
  sourceUrl: string;
  acquisitionDate: string;
  notes?: string;
}

/**
 * Get link impact analysis for a specific link event
 */
export const getLinkImpact = async (
  linkEventId: string,
  params: Omit<LinkImpactParams, 'pageId'>
): Promise<LinkImpactData> => {
  const { engine = 'google', baselineDays = 30, outcomeDays = 30 } = params;
  const response = await api.get(`/coreapi/link-impact/${linkEventId}`, {
    params: { engine, baselineDays, outcomeDays },
  });
  return response.data;
};

/**
 * Get all link impact analyses for a page
 */
export const getPageLinkImpacts = async (
  params: LinkImpactParams
): Promise<LinkImpactData[]> => {
  const { pageId, engine = 'google', baselineDays = 30, outcomeDays = 30 } = params;
  const response = await api.get(`/coreapi/pages/${pageId}/link-impacts`, {
    params: { engine, baselineDays, outcomeDays },
  });
  return response.data;
};

/**
 * Create a manual link event (for tracking)
 */
export const createLinkEvent = async (
  params: LinkEventCreateParams
): Promise<LinkEvent> => {
  const response = await api.post('/coreapi/link-events', params);
  return response.data;
};

/**
 * Recompute link impact analysis
 */
export const recomputeLinkImpact = async (
  linkEventId: string,
  params: Omit<LinkImpactParams, 'pageId'>
): Promise<LinkImpactData> => {
  const { engine = 'google', baselineDays = 30, outcomeDays = 30 } = params;
  const response = await api.post(`/coreapi/link-impact/${linkEventId}/recompute`, null, {
    params: { engine, baselineDays, outcomeDays },
  });
  return response.data;
};

/**
 * Get link events for a page
 */
export const getPageLinkEvents = async (pageId: string): Promise<LinkEvent[]> => {
  const response = await api.get(`/coreapi/pages/${pageId}/link-events`);
  return response.data;
};

/**
 * Delete a link event
 */
export const deleteLinkEvent = async (linkEventId: string): Promise<void> => {
  await api.delete(`/coreapi/link-events/${linkEventId}`);
};
